import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useGetByID from '../../api/v1/accounts/useGetByID'
import { UserTypes } from '../../types/Account'
import { decodeJwt, extractAccountIDFromJwt, getUserType, isExpired } from '../../utils/jwt/jwt'
import { AlertData, AlertType, useAlert } from '../alert'
import type { AuthState, UserInfo } from './AuthContext'
import AuthContext from './AuthContext'

export const sessionItemKey = 'vpc_login_info'

export type SessionData = {
  token: string
  exp: number
  userInfo: UserInfo
}

/**
 * AuthProvider is a React context provider responsible for:
 * - Managing the authentication state (`authState`) of the application.
 * - Fetching and updating user data from the service based on a valid JWT.
 * - Providing utility functions (`setAuthToken`, `refreshAuthState`, `logout`) to manage authentication.
 *
 * @param {Object} props - The props for the AuthProvider component.
 * @param {ReactNode} props.children - The child components to be wrapped by the provider.
 *
 * @return {JSX.Element} The `AuthContext.Provider` component that provides:
 * - `authState`: The current authentication state of the user.
 *     - `jwt`: The decoded JSON Web Token (JWT) object containing authentication details.
 *     - `jwtRaw`: The raw JWT string used for API requests.
 *     - `userType`: The type of user (e.g., requestor, approver, or unknown).
 *     - `isLoading`: Indicates whether user data is currently being fetched.
 *     - `accountInfo`: The user's account information retrieved from the service.
 * - `setAuthToken`: Function to set a new JWT and update the state.
 * - `refreshAuthState`: Function to retrieve the JWT from session storage and update the state.
 * - `logout`: Function to log the user out.
 */
const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // -------- Setup State -------- //
  const { showAlert } = useAlert()
  const { t } = useTranslation()

  const [authState, setAuthState] = useState<AuthState>({
    jwt: null,
    jwtRaw: '',
    userType: UserTypes.unk,
    isLoading: false,
    userInfo: { username: '', suffix: '' },
  })

  // initialize the call to get user information based on the jwt token
  const { data, loading, error, callFetch } = useGetByID(
    extractAccountIDFromJwt(authState.jwt),
    authState.jwtRaw,
  )

  // -------- Hooks -------- //

  // Check for a token when the provider mounts
  useEffect(() => {
    refreshAuthState()
  }, [])

  // get user information if a jwt is present
  useEffect(() => {
    if (authState.jwt && !authState.accountInfo && !authState.isLoading && !error) {
      if (isExpired(authState.jwt.payload.exp)) {
        console.error('Token expired')
        logout()
      }
      setAuthState((prevAuthState) => ({
        ...prevAuthState,
        isLoading: true,
      }))

      callFetch()
    }
  }, [authState, callFetch, loading])

  // show alert if an error occurs
  useEffect(() => {
    if (!error) {
      return
    }

    const alert: AlertData = {
      msg: t('vpc:auth.fetch.error'),
      severity: AlertType.ERROR,
      duration: 5000,
    }
    showAlert(alert)
    const timeoutId = setTimeout(() => {
      logout()
    }, 4000)

    // clear the timeout if the component is unmounted
    return () => {
      clearTimeout(timeoutId)
    }
  }, [error])

  // set auth state when user information is returned
  useEffect(() => {
    if (data && authState.accountInfo != data.account && authState.jwt && !loading && !error) {
      const userType = getUserType(authState.jwt.payload)

      setAuthState({
        ...authState,
        accountInfo: data.account,
        isLoading: false,
        userType,
      })
    }
  }, [authState, data, loading, error])

  // -------- Context Functions -------- //

  // Clears the authentication state and session storage data
  const logout = useCallback(() => {
    sessionStorage.removeItem(sessionItemKey)
    setAuthState({
      jwt: null,
      jwtRaw: '',
      userType: UserTypes.unk,
      isLoading: false,
      accountInfo: undefined,
      userInfo: { username: '', suffix: '' },
    })

    // if approver, reload page, else route to home
    if (window.location.pathname === '/approve') {
      window.location.reload()
    } else {
      window.location.href = '/'
    }
  }, [])

  // Sets or resets the auth token in session storage
  const setAuthToken = useCallback(
    (token: string, userInfo: UserInfo) => {
      try {
        const jwt = decodeJwt(token)

        const sessionData: SessionData = {
          token,
          exp: jwt.payload.exp,
          userInfo,
        }

        sessionStorage.setItem(sessionItemKey, JSON.stringify(sessionData))

        const userType = getUserType(jwt.payload)
        setAuthState((prevAuth) => ({
          ...prevAuth,
          jwt,
          jwtRaw: token,
          userType,
          userInfo,
        }))
      } catch (err) {
        if (err instanceof Error) {
          console.error('Failed to set auth token:', err)
        } else {
          console.error('Failed to set auth token: Unknown error')
        }
        logout()
      }
    },
    [logout],
  )

  // refreshAuthState checks for authentication state by reading the "vpc-jwt" session storage data.
  //   If the token is present and not expired, it decodes the JWT and updates the auth state.
  //   If the token is expired, it logs the user out.
  const refreshAuthState = useCallback(() => {
    const sessionItem = sessionStorage.getItem(sessionItemKey)

    if (sessionItem) {
      try {
        const { token, exp, userInfo } = JSON.parse(sessionItem)

        if (isExpired(exp)) {
          throw new Error('Token expired')
        }

        const jwt = decodeJwt(token)
        const userType = getUserType(jwt.payload)

        setAuthState({
          jwt,
          jwtRaw: token,
          userType,
          isLoading: false,
          accountInfo: undefined,
          userInfo,
        })
      } catch {
        logout()
      }
    }
  }, [logout])

  return (
    <AuthContext.Provider value={{ authState, setAuthToken, logout, refreshAuthState }}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
